import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGetProfile } from '../features/profile/queries';
import { useUpdateMarketingOptinsMutation } from '../features/marketing/queries';
import { useAuth } from '@/lib/auth';

/**
 * One-time backfill prompt for authenticated users who haven't yet
 * recorded a marketing-consent decision.
 *
 * Visibility gate: ONLY shown when `profile.marketing_state` is null —
 * both "Yes, sign me up" and "No thanks" create the MARKETING_STATE row,
 * which causes this banner to stop showing on the next render. Design
 * trade-off: there is no "ask me later" path; either decision is final
 * until the user revisits the Notifications section.
 *
 * `profile.marketing_state` also resolves to null on a transient DDB
 * read error (per Phase 2a's `fetchMarketingStateSafe`), so the banner
 * may briefly reappear after an error — accepted UX.
 */
export function MarketingOptinBanner(): JSX.Element | null {
  const { authStatus } = useAuth();
  const enabled = authStatus === 'authenticated';
  const { data: profile, isLoading } = useGetProfile(enabled);
  const mutation = useUpdateMarketingOptinsMutation();

  if (!enabled) return null;
  if (isLoading) return null;
  if (profile?.marketing_state) return null;

  const handle = (yes: boolean) => () => {
    mutation.mutate(
      { optin_general: yes, optin_drops: yes },
      {
        onError: (err) => {
          toast.error('Something went wrong. Please try again.');
          console.error('[banner] updateMarketingOptins failed', err);
        },
      },
    );
  };

  return (
    <Card data-testid="marketing-optin-banner" className="my-4">
      <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
        <p className="text-body">Stay in the loop on new pack drops?</p>
        <div className="flex gap-2">
          <Button onClick={handle(true)} disabled={mutation.isPending}>
            Yes, sign me up
          </Button>
          <Button variant="outline" onClick={handle(false)} disabled={mutation.isPending}>
            No thanks
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
